import React, { useCallback } from "react";

import clsx from "clsx";
import { useToasts } from "react-toast-notifications";

import { SecondaryIconButton } from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useShortlinkToClipboard } from "@features/shortlinks/hooks";
import { useShortLinksContext } from "@features/shortlinks/provider";
import { useHasFeatureFlag } from "@hooks/http";
import { IconButton } from "@shared/buttons";

export const ShareButton = ({ compact }: { compact?: boolean }) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { addToast } = useToasts();
  const { createShortlinkLoading } = useShortLinksContext();

  const hasFeatureFlags = useHasFeatureFlag();
  const { shortlinkCopyToClipboard } = useShortlinkToClipboard();
  const copyUrl = useCallback(async () => {
    prettyLogEventPage({
      domain: "Share Button",
      action: "onClick",
    });

    try {
      if (!hasFeatureFlags("enableShortlinks")) {
        await navigator.clipboard.writeText(window.location.href);
        addToast("Copied to clipboard!", { appearance: "success" });

        return;
      }
      await shortlinkCopyToClipboard();
      addToast("Copied to clipboard!", { appearance: "success" });
    } catch (err) {
      addToast("Error while coping to clipboard!", {
        appearance: "error",
      });
    }
  }, [addToast, hasFeatureFlags, prettyLogEventPage, shortlinkCopyToClipboard]);

  return compact ? (
    <SecondaryIconButton
      loading={createShortlinkLoading}
      onClick={async () => {
        await copyUrl();
      }}
    >
      {!createShortlinkLoading && (
        <i
          className={clsx(
            "fa-regular fa-paper-plane text-sm text-v2-secondary group-hover:text-v2-action",
          )}
        />
      )}
    </SecondaryIconButton>
  ) : (
    <IconButton
      variant="secondary"
      leftIcon={<i className="fa-sharp fa-regular fa-link mr-1.5" />}
      className="font-bold"
      loading={createShortlinkLoading}
      onClick={async () => {
        await copyUrl();
      }}
    >
      SHARE
    </IconButton>
  );
};
